import React, { useEffect } from "react";
import { useParams, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useTranslation } from "react-i18next";
import { getDasUrl } from "actions/config";
import { processActions } from "components/Design/DesignUtils";
import { useDispatch } from "react-redux";
import { setSelectedLanding } from "actions/hotel";
import { useStats } from "contexts/stats";
import { STATS_EVENT_TYPES } from "zstats";
import { resolveStatsTag } from "utils/statistics";
import { useSelector } from "react-redux";

const InterestPointsPreview = ({
    points,
    active,
    generalStyles,
    buttonStyles,
    setFixedHeight,
    fixedHeight,
    scrollEnabled,
    setHasFixedHeight,
    hasFixedHeight,
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const { id, mainMapId, depth } = useParams();

    const pointData = points?.find((point) => point.id === active);
    const stats = useStats();

    useEffect(() => {
        if (hasContent(pointData)) {
            setHasFixedHeight(true);
        } else {
            setHasFixedHeight(false);
        }
    }, [pointData]);

    if (!pointData) return null;

    const { name, description, image, navigationAction } = pointData;
    const imageUrl = image?.libraryRef ? `${getDasUrl()}/${image.libraryRef}` : image?.externalUrl;

    const isValidAction = navigationAction && navigationAction?.type !== "disabled";
    const isMapNavigation = navigationAction?.type === "map";
    const isHomeLandingNavigation = navigationAction?.type === "landing" && navigationAction?.value === "landing";
    const isContentNavigation = navigationAction?.type === "content";

    const theme = window.appConfig?.theme || {};
    const themeAccent = theme?.accent || "#000";

    // console.log(theme);

    const resolvedMainMapId = mainMapId || id;
    const resolvedDepth = Number(depth || 0) + 1;

    const goToLanding = (landingId) => {
        const path =
            landingId === "landing" ? "/site" : `/site?&mapLandingRef=${landingId}&&mainMap=${resolvedMainMapId}`;
        history.push(path);
        location.reload();
    };

    const link = isValidAction
        ? processActions({
              data: {
                  actions: [{ ...navigationAction, value: String(navigationAction.value) }],
              },
              goToLanding,
              dasURL: getDasUrl(),
              id: "interest-point-link",
              setSelectedLanding: (l) => {
                  dispatch(setSelectedLanding(l));
              },
          })
        : null;

    const handleNavigation = (e) => {
        const statsTag = resolveStatsTag(navigationAction, `${pointData?.id}_PointOfInterest_Maps`);

        if (statsTag) {
            stats.addEvent({ eventName: statsTag, type: STATS_EVENT_TYPES.CLICK });
        }

        if (link?.type === "library") {
            stats.addEvent({
                type: STATS_EVENT_TYPES.SECTION,
                eventName: "library",
            });
        }

        if (link?.href === "#") e.preventDefault();
        if (isHomeLandingNavigation) history.push("/site");
        if (isMapNavigation) {
            history.push(`/maps/${navigationAction.value}/${resolvedMainMapId}/${resolvedDepth}`);
        }
    };

    const previewBottomInset = "env(safe-area-inset-bottom, 0px)";

    return (
        <div
            // ref={scrollContainerRef}
            className={`${!hasFixedHeight ? "flex flex-col justify-between" : ""} `}
            style={{
                boxSizing: "border-box",
                height: hasFixedHeight ? "auto" : "100%",
                paddingBottom: previewBottomInset,
            }}
        >
            <div className="font-bold text-lg mb-2" style={{ color: generalStyles?.fgColor || "#000" }}>
                {name}
            </div>

            {imageUrl && (
                <img src={imageUrl} alt={name} className="w-full pointer-events-none object-cover rounded mb-2" />
            )}

            <div className="whitespace-pre-line" style={{ color: generalStyles?.fgColor || "#000" }}>
                {description}
            </div>

            {isValidAction && link && (
                <a
                    className="w-full"
                    id={link.id}
                    href={!isMapNavigation ? link.href : null}
                    target={link.target}
                    onClick={isContentNavigation && !isMapNavigation ? link?.onClick : handleNavigation}
                >
                    <button
                        className="w-full btn-accent mt-5 font-bold py-2 rounded"
                        style={{
                            backgroundColor: buttonStyles?.bgColor || themeAccent,
                            color: "#fff",
                        }}
                    >
                        {t("show-more")}
                    </button>
                </a>
            )}
        </div>
    );
};

const hasContent = (point) => {
    if (!point || typeof point !== "object") return false;
    const hasDescription = typeof point.description === "string" && point.description.trim() !== "";
    const image = point.image;
    const hasValidImage =
        image &&
        typeof image === "object" &&
        ((typeof image.externalUrl === "string" && image.externalUrl.trim() !== "") ||
            (typeof image.libraryRef === "string" && image.libraryRef.trim() !== ""));
    return hasDescription || hasValidImage;
};

export default InterestPointsPreview;
